import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  useCreateWhiteLabelCompanyMutation,
  useGetWhiteLabelCompanyQuery,
  useUpdateWhiteLabelCompanyMutation,
} from "../../../redux/admin/whiteLabelApi";
import { toast } from "react-hot-toast";

const EMPTY_FORM = Object.freeze({
  companyName: "",
  companyCode: "",
  status: "active",
  description: "",
  primaryDomain: "",
  supportEmail: "",
});

const COMPANY_CODE_PATTERN = /^[A-Za-z0-9_-]+$/;

const normalizeForm = (company) => ({
  companyName: company?.companyName || "",
  companyCode: company?.companyCode || "",
  status: company?.status || "active",
  description: company?.description || "",
  primaryDomain: company?.domains?.primary || "",
  supportEmail: company?.support?.email || "",
});

const mapValidationErrors = (error) => {
  const nextErrors = {};
  const items = Array.isArray(error?.data?.errors) ? error.data.errors : [];

  items.forEach((item) => {
    const path = String(item?.path || "");
    if (path === "companyName") nextErrors.companyName = item.msg;
    if (path === "companyCode") nextErrors.companyCode = item.msg;
    if (path === "status") nextErrors.status = item.msg;
    if (path === "description") nextErrors.description = item.msg;
    if (path === "domains.primary") nextErrors.primaryDomain = item.msg;
    if (path === "support.email") nextErrors.supportEmail = item.msg;
  });

  return nextErrors;
};

function buildPayload(form, mode) {
  const payload = {
    companyName: String(form.companyName || "").trim(),
    status: form.status || "active",
    description: String(form.description || "").trim(),
  };

  const primaryDomain = String(form.primaryDomain || "").trim();
  const supportEmail = String(form.supportEmail || "").trim();

  if (mode === "create") {
    const companyCode = String(form.companyCode || "").trim();
    if (companyCode) payload.companyCode = companyCode;
  }

  if (primaryDomain) payload.domains = { primary: primaryDomain };
  if (supportEmail) payload.support = { email: supportEmail };

  return payload;
}

function validateForm(form, mode) {
  const errors = {};
  const companyName = String(form.companyName || "").trim();
  const companyCode = String(form.companyCode || "").trim();
  const supportEmail = String(form.supportEmail || "").trim();

  if (!companyName) {
    errors.companyName = "Company name is required.";
  } else if (companyName.length < 2 || companyName.length > 160) {
    errors.companyName = "Company name must be between 2 and 160 characters.";
  }

  if (mode === "create" && companyCode) {
    if (companyCode.length < 2 || companyCode.length > 40) {
      errors.companyCode = "Company code must be between 2 and 40 characters.";
    } else if (!COMPANY_CODE_PATTERN.test(companyCode)) {
      errors.companyCode =
        "Company code may contain only letters, numbers, underscores, and hyphens.";
    }
  }

  if (supportEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(supportEmail)) {
    errors.supportEmail = "Support email must be a valid email address.";
  }

  return errors;
}

export default function WhiteLabelCompanyDialog({
  open,
  mode,
  companyId,
  onClose,
}) {
  const theme = useTheme();
  const isEditMode = mode === "edit";
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  const {
    data: company,
    isFetching: isFetchingCompany,
    error: fetchError,
  } = useGetWhiteLabelCompanyQuery(companyId, {
    skip: !open || !isEditMode || !companyId,
  });

  const [createCompany, { isLoading: isCreating }] =
    useCreateWhiteLabelCompanyMutation();
  const [updateCompany, { isLoading: isUpdating }] =
    useUpdateWhiteLabelCompanyMutation();

  const isSubmitting = isCreating || isUpdating;

  useEffect(() => {
    if (!fetchError) return;
    toast.error(fetchError?.data?.msg || "Failed to load company details.");
  }, [fetchError]);

  useEffect(() => {
    if (!open) {
      setForm(EMPTY_FORM);
      setFieldErrors({});
      setSubmitError("");
      return;
    }

    if (isEditMode) {
      if (company) {
        setForm(normalizeForm(company));
        setFieldErrors({});
        setSubmitError("");
      }
      return;
    }

    setForm(EMPTY_FORM);
    setFieldErrors({});
    setSubmitError("");
  }, [open, isEditMode, company]);

  const dialogTitle = useMemo(
    () => (isEditMode ? "Edit Company" : "Create Company"),
    [isEditMode]
  );

  const dialogSubtitle = isEditMode
    ? "Update company profile details without affecting GPS functionality."
    : "Create a new white-label company using the existing backend validation rules.";

  const handleFieldChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    setSubmitError("");
  };

  const handleSubmit = async () => {
    const nextErrors = validateForm(form, mode);
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }

    try {
      const payload = buildPayload(form, mode);

      if (isEditMode) {
        await updateCompany({ companyId, ...payload }).unwrap();
        toast.success("Company updated successfully.");
      } else {
        await createCompany(payload).unwrap();
        toast.success("Company created successfully.");
      }

      onClose(true);
    } catch (error) {
      const apiFieldErrors = mapValidationErrors(error);
      const message =
        error?.data?.msg ||
        error?.data?.message ||
        `Unable to ${isEditMode ? "update" : "create"} company.`;
      if (Object.keys(apiFieldErrors).length > 0) {
        setFieldErrors(apiFieldErrors);
      }
      setSubmitError(message);
      toast.error(message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => (isSubmitting ? null : onClose())}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundImage: "none",
          bgcolor: alpha(theme.palette.background.paper, 0.98),
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 900 }}>
          {dialogTitle}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          {dialogSubtitle}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {isEditMode && isFetchingCompany ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1.5}
            sx={{ minHeight: 260 }}
          >
            <CircularProgress size={28} />
            <Typography color="text.secondary">
              Loading company details...
            </Typography>
          </Stack>
        ) : fetchError ? (
          <Alert severity="error">
            {fetchError?.data?.msg || "Failed to load company details."}
          </Alert>
        ) : (
          <Stack spacing={2}>
            {submitError ? <Alert severity="error">{submitError}</Alert> : null}

            <TextField
              label="Company Name"
              value={form.companyName}
              onChange={handleFieldChange("companyName")}
              error={Boolean(fieldErrors.companyName)}
              helperText={fieldErrors.companyName || " "}
              fullWidth
              required
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Company Code"
                value={form.companyCode}
                onChange={handleFieldChange("companyCode")}
                error={Boolean(fieldErrors.companyCode)}
                helperText={
                  fieldErrors.companyCode ||
                  (isEditMode
                    ? "Company code is locked after creation."
                    : "Optional. Leave empty to auto-generate.")
                }
                fullWidth
                disabled={isEditMode}
              />

              <TextField
                select
                label="Status"
                value={form.status}
                onChange={handleFieldChange("status")}
                error={Boolean(fieldErrors.status)}
                helperText={fieldErrors.status || " "}
                fullWidth
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Stack>

            <TextField
              label="Primary Domain"
              value={form.primaryDomain}
              onChange={handleFieldChange("primaryDomain")}
              error={Boolean(fieldErrors.primaryDomain)}
              helperText={
                fieldErrors.primaryDomain ||
                "Optional. Example: tracking.example.com"
              }
              fullWidth
            />

            <TextField
              label="Support Email"
              value={form.supportEmail}
              onChange={handleFieldChange("supportEmail")}
              error={Boolean(fieldErrors.supportEmail)}
              helperText={fieldErrors.supportEmail || " "}
              fullWidth
            />

            <TextField
              label="Description"
              value={form.description}
              onChange={handleFieldChange("description")}
              error={Boolean(fieldErrors.description)}
              helperText={fieldErrors.description || " "}
              multiline
              minRows={4}
              fullWidth
            />
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={() => onClose()} disabled={isSubmitting}>
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || (isEditMode && (isFetchingCompany || Boolean(fetchError)))}
        >
          {isSubmitting
            ? isEditMode
              ? "Saving..."
              : "Creating..."
            : isEditMode
              ? "Save Changes"
              : "Create Company"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
